// The closed catalog of things an offer summary can say.
//
// CONSUMER: shared (Workers extraction route + web review panel + tests).
//
// WHY A CLOSED LIST. Nate dictates an offer and the OS proposes values
// for the blanks on the AAR forms. An open extractor — "pull out
// whatever you find" — produces `sellerRentBack` on Tuesday and
// `seller_rent_back_days` on Wednesday, and nothing downstream can bind
// to either. Worse, it has no defined behaviour to test. A closed list
// means "what can this feature put on a contract?" is a file a person
// can read, and every entry has a named test.
//
// WHY EACH SLOT NAMES A MERGE TOKEN. The model never chooses which
// blank on which form gets the price — that would be the dangerous
// version of this feature. A slot reaches a specific blank through the
// merge-token binding a HUMAN authored when they placed the field, which
// the saved layout then remembers per form. This column is that join,
// and it is the only one.
//
// Pure and dependency-free — same rule as field-types.ts.

/** How a slot's value is written down, which decides how it is parsed
 *  and, more importantly, when it must be treated as ambiguous. */
export type OfferSlotType =
  | "money"
  | "date"
  | "integer_days"
  | "boolean"
  | "enum"
  | "text"
  /** Deliberately dual — "ten down" is $10,000 or 10% and both are
   *  ordinary things to say out loud. Collapsing them at parse time is
   *  exactly the invisible-wrong-number failure this design exists to
   *  stop, so these are ALWAYS ambiguous unless a unit was spoken. */
  | "money_or_percent";

export interface OfferSlot {
  key: string;
  /** What a person calls it. Never the key. */
  label: string;
  type: OfferSlotType;
  /** The merge token that carries this value onto a form. A field bound
   *  to this token prints the accepted value at send time. */
  token: string;
  /** Allowed values, for `enum` slots only. */
  options?: readonly string[];
  /** Shown in the review panel and given to the model as an example of
   *  the SHAPE of an answer, never as a default. */
  example: string;
  /** Told to the model so it knows what this blank means on an AAR
   *  form. Kept here rather than in the prompt so the catalog is the one
   *  place a slot is defined. */
  hint: string;
}

export const LOAN_TYPES = ["Conventional", "FHA", "VA", "USDA", "Cash", "Other"] as const;

/**
 * v1 slots. Ordered the way an agent talks through an offer, which is
 * also the order the review panel lists them — price first, because it
 * is both the most important number and the one most often said
 * ambiguously.
 */
export const OFFER_SLOTS: readonly OfferSlot[] = [
  {
    key: "purchase_price",
    label: "Purchase price",
    type: "money",
    token: "offer.purchase_price",
    example: "$350,000",
    hint: "What the buyer is offering for the property.",
  },
  {
    key: "earnest_money",
    label: "Earnest money",
    type: "money",
    token: "offer.earnest_money",
    example: "$5,000",
    hint: "The deposit that goes to escrow with the offer.",
  },
  {
    key: "close_of_escrow_date",
    label: "Close of escrow",
    type: "date",
    token: "offer.close_of_escrow_date",
    example: "October 15, 2026",
    hint:
      "The closing date. If the summary says a number of days rather than a date, that is an " +
      "INTERPRETATION — and note that days usually run from contract acceptance, not from today.",
  },
  {
    key: "possession",
    label: "Possession",
    type: "text",
    token: "offer.possession",
    example: "At close of escrow",
    hint: "When the buyer gets the keys.",
  },
  {
    key: "loan_type",
    label: "Loan type",
    type: "enum",
    token: "offer.loan_type",
    options: LOAN_TYPES,
    example: "Conventional",
    hint: "How the purchase is financed.",
  },
  {
    key: "loan_amount",
    label: "Loan amount",
    type: "money",
    token: "offer.loan_amount",
    example: "$280,000",
    hint: "The amount being borrowed.",
  },
  {
    key: "down_payment",
    label: "Down payment",
    type: "money_or_percent",
    token: "offer.down_payment",
    example: "20% or $70,000",
    hint:
      "How much the buyer is putting down. ALWAYS ambiguous unless the summary says a unit — " +
      "'ten down' could be $10,000 or 10%.",
  },
  {
    key: "seller_concessions",
    label: "Seller concessions",
    type: "money",
    token: "offer.seller_concessions",
    example: "$3,500",
    hint: "What the seller is being asked to contribute toward closing costs.",
  },
  {
    key: "repair_credit",
    label: "Repair credit",
    type: "money",
    token: "offer.repair_credit",
    example: "$1,200",
    hint: "A credit in place of repairs.",
  },
  {
    key: "inspection_period_days",
    label: "Inspection period",
    type: "integer_days",
    token: "offer.inspection_period_days",
    example: "10 days",
    hint:
      "The buyer's due-diligence window, in days. 'The standard period' is an INTERPRETATION — " +
      "this brokerage's standard is not something you can know.",
  },
  {
    key: "appraisal_contingency",
    label: "Appraisal contingency",
    type: "boolean",
    token: "offer.appraisal_contingency",
    example: "Yes",
    hint: "Whether the offer is contingent on the appraisal.",
  },
  {
    key: "financing_contingency",
    label: "Financing contingency",
    type: "boolean",
    token: "offer.financing_contingency",
    example: "Yes",
    hint: "Whether the offer is contingent on the buyer's loan.",
  },
  {
    key: "home_warranty",
    label: "Home warranty",
    type: "text",
    token: "offer.home_warranty",
    example: "$600, paid by seller",
    hint: "Home warranty terms, if the offer asks for one.",
  },
  {
    key: "personal_property_included",
    label: "Personal property included",
    type: "text",
    token: "offer.personal_property_included",
    example: "Refrigerator, washer and dryer",
    hint: "Items conveying with the house that would not otherwise.",
  },
  {
    key: "offer_expires",
    label: "Offer expires",
    type: "date",
    token: "offer.offer_expires",
    example: "September 3, 2026 at 5:00 PM",
    hint: "When the offer comes off the table.",
  },
];

const BY_KEY = new Map(OFFER_SLOTS.map((s) => [s.key, s]));
const BY_TOKEN = new Map(OFFER_SLOTS.map((s) => [s.token, s]));

export function findOfferSlot(key: string): OfferSlot | null {
  return BY_KEY.get(key) ?? null;
}

export function findOfferSlotByToken(token: string): OfferSlot | null {
  return BY_TOKEN.get(token) ?? null;
}

export function isOfferSlotKey(v: unknown): v is string {
  return typeof v === "string" && BY_KEY.has(v);
}

/** Every `offer.*` merge token, for the merge-field catalog. */
export const OFFER_SLOT_TOKENS: readonly string[] = OFFER_SLOTS.map((s) => s.token);

// ─── Provenance ────────────────────────────────────────────────────
//
// How a stored value came to be. Three states, kept distinct because
// "the model said it and he agreed" and "the model said it and he
// fixed it" are different facts about the same number, and an audit
// row that blurs them answers the wrong question later.

export type OfferValueProvenance =
  /** Proposed by Stew, accepted unchanged. */
  | "ai_accepted"
  /** Proposed by Stew, changed by the advisor before accepting. */
  | "ai_edited"
  /** Typed by the advisor; Stew proposed nothing here. */
  | "typed";

export function isOfferValueProvenance(v: unknown): v is OfferValueProvenance {
  return v === "ai_accepted" || v === "ai_edited" || v === "typed";
}

// ─── How sure, and why that is not a number ────────────────────────
//
// The design's central claim: CONFIDENCE IS THE WRONG GATE,
// INTERPRETATION IS THE RIGHT ONE. A model repeating a number the
// advisor said out loud is safe even at middling confidence. A model
// that CONVERTED something is dangerous even at high confidence —
// "three fifty" formatted as $350,000 is confident, plausible, and
// possibly $3.50 a square foot.
//
// So there is no 0-1 score anywhere in this pipeline. There are three
// named states, and only one of them starts checked.

export type OfferReadingKind =
  /** The advisor stated this value in this form. No conversion. */
  | "heard"
  /** The model converted, expanded, or inferred to get here. */
  | "interpreted"
  /** More than one defensible reading of what was said. */
  | "ambiguous";

export function isOfferReadingKind(v: unknown): v is OfferReadingKind {
  return v === "heard" || v === "interpreted" || v === "ambiguous";
}

/**
 * Whether a proposal starts checked in the review panel.
 *
 * ONLY a value the advisor literally said, unconverted, starts checked.
 * Everything else is theirs to look at first. This function is the
 * whole gate, which is why it is one line with a name rather than an
 * inline `=== "heard"` in a component.
 */
export function startsChecked(reading: OfferReadingKind): boolean {
  return reading === "heard";
}
